import { BookOpen, Lightbulb, ListTree, PencilLine, Quote, Sparkles } from 'lucide-react';
import { CLAUSE_TYPE_LABELS, type SentenceAnalysisResult } from '@en3/shared';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * 长难句解析卡片 —— **阅读页与长难句实验室共用同一个渲染器**。
 *
 * 为什么必须共用：解析结果有 8 个组成部分（主干/修饰/从句/语法点/翻译/步骤/建议/仿写），
 * 两个页面各写一遍的结果一定是"阅读页有仿写、实验室没有"这种不一致。
 *
 * 卡片里有两个诚实性设计，不要删：
 * 1. `generatedBy === 'rule'` 时显示**「基础解析」**徽章并解释原因。
 *    规则解析与模型解析的质量差距很大，把它伪装成 AI 结果是对用户的误导。
 * 2. `translation` 为空时显示"翻译需要 AI"而不是空白 —— 空白会被理解成"这句话没意思"。
 */
export function AnalysisCard({
  result,
  onWordClick,
  className,
}: {
  result: SentenceAnalysisResult;
  /** 点击关键词时回调（阅读页用它在同一处打开查词卡片） */
  onWordClick?: (word: string) => void;
  className?: string;
}) {
  const { analysis, features, generatedBy, provider, cached, notes } = result;
  const isRule = generatedBy === 'rule';

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles aria-hidden className="size-4 text-primary" />
          长难句解析
        </CardTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={isRule ? 'warning' : 'primary'}>{isRule ? '基础解析（规则）' : 'AI 解析'}</Badge>
          {cached && <Badge tone="muted">缓存</Badge>}
          <Badge tone="muted">难度 {Math.round(features.score)}</Badge>
          {!isRule && provider !== 'mock' && <Badge tone="muted">{provider}</Badge>}
        </div>
      </CardHeader>

      <CardBody className="space-y-5 text-sm">
        {/* 原句 */}
        <section>
          <p className="reader-prose rounded-md bg-muted/50 px-3 py-2 font-serif">{analysis.text}</p>
          {features.notes.length > 0 && (
            <p className="mt-1.5 text-xs text-muted-foreground">判定依据：{features.notes.join('；')}</p>
          )}
        </section>

        {/* 主干 */}
        <section>
          <SectionTitle icon={<ListTree aria-hidden className="size-4" />} title="句子主干" />
          <p className="mt-1.5 rounded-md border-l-2 border-primary bg-accent/40 px-3 py-2">
            {analysis.mainClause}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            先把主干读通，再把下面这些成分一层层加回去。
          </p>
        </section>

        {/* 从句 */}
        {analysis.clauses.length > 0 && (
          <section>
            <SectionTitle title={`从句与结构（${analysis.clauses.length}）`} />
            <ul className="mt-1.5 space-y-2">
              {analysis.clauses.map((clause, index) => (
                <li key={`${clause.marker}-${index}`} className="rounded-md border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone="primary">{CLAUSE_TYPE_LABELS[clause.type] ?? clause.type}</Badge>
                    {clause.marker && <code className="text-xs text-muted-foreground">{clause.marker}</code>}
                  </div>
                  <p className="mt-1.5">{clause.text}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{clause.role}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 修饰成分 */}
        {analysis.modifiers.length > 0 && (
          <section>
            <SectionTitle title="修饰与插入成分" />
            <ul className="mt-1.5 space-y-1.5">
              {analysis.modifiers.map((modifier, index) => (
                <li key={`${modifier.text}-${index}`} className="text-sm">
                  <span className="rounded bg-muted px-1.5 py-0.5">{modifier.text}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{modifier.role}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 语法点 */}
        {analysis.grammarPoints.length > 0 && (
          <section>
            <SectionTitle icon={<BookOpen aria-hidden className="size-4" />} title="语法点" />
            <ul className="mt-1.5 space-y-2">
              {analysis.grammarPoints.map((point) => (
                <li key={point.title}>
                  <p className="font-medium">{point.title}</p>
                  <p className="text-xs text-muted-foreground">{point.detail}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 翻译 */}
        <section>
          <SectionTitle title="中文翻译" />
          {analysis.translation ? (
            <p className="mt-1.5">{analysis.translation}</p>
          ) : (
            <p className="mt-1.5 text-xs text-muted-foreground">
              暂未生成翻译（当前展示的是规则版解析，翻译需要 AI 通道可用）。
              可先按上面的"拆解步骤"自己译一遍，再对照参考答案。
            </p>
          )}
        </section>

        {/* 拆解步骤 */}
        {analysis.steps.length > 0 && (
          <section>
            <SectionTitle title="怎么一步步读懂" />
            <ol className="mt-1.5 space-y-1.5">
              {analysis.steps.map((step, index) => (
                <li key={step} className="flex gap-2">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] text-primary">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* 学习方法 */}
        {analysis.studyTips.length > 0 && (
          <section>
            <SectionTitle icon={<Lightbulb aria-hidden className="size-4" />} title="练法建议" />
            <ul className="mt-1.5 space-y-1.5">
              {analysis.studyTips.map((tip) => (
                <li key={tip} className="flex gap-2">
                  <span aria-hidden className="text-primary">
                    •
                  </span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 仿写 */}
        {analysis.imitation.length > 0 && (
          <section>
            <SectionTitle icon={<PencilLine aria-hidden className="size-4" />} title="仿写练习" />
            <ul className="mt-1.5 space-y-2">
              {analysis.imitation.map((item) => (
                <li key={item.pattern} className="rounded-md border px-3 py-2">
                  <p className="font-mono text-xs text-primary">{item.pattern}</p>
                  <p className="mt-1.5 flex gap-1.5">
                    <Quote aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                    <span>{item.example}</span>
                  </p>
                  {item.exampleZh && <p className="mt-0.5 text-xs text-muted-foreground">{item.exampleZh}</p>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 关键词 */}
        {analysis.keyWords.length > 0 && (
          <section>
            <SectionTitle title="值得记的词" />
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {analysis.keyWords.map((word) => (
                <button
                  key={word.surface}
                  type="button"
                  onClick={() => onWordClick?.(word.surface)}
                  disabled={!onWordClick}
                  className="rounded-full border px-2.5 py-1 text-xs hover:bg-accent hover:text-accent-foreground disabled:cursor-default disabled:hover:bg-transparent"
                >
                  <span className="font-medium">{word.surface}</span>
                  {word.zh && <span className="ml-1.5 text-muted-foreground">{word.zh}</span>}
                  {word.cefrLevel && <span className="ml-1.5 text-[10px] text-primary">{word.cefrLevel}</span>}
                </button>
              ))}
            </div>
          </section>
        )}

        {notes.length > 0 && (
          <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">{notes.join('；')}</p>
        )}
      </CardBody>
    </Card>
  );
}

function SectionTitle({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
      {icon}
      {title}
    </h3>
  );
}
